import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import CustomStyledMenu from "../../../../components/Menu/CustomStyledMenu";
import SearchField from "../../../../components/InputFields/SearchField";
import { Button } from "../../../../components/Buttons";
import IconComponent from "../../../../components/Icon/IconComponent";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
} from "../../../../utils/constants";
import { CampaignSettingsEndpoints } from "../../../../utils/constants/httpConstants";
import { postRequest } from "../../../../Apis/apiWrapper";
import { getErrorMessage } from "../../../../utils/helpers";
import { GREY, WARNING } from "../../../../utils/constants/colors";
import { fontSize, ICON_LABELS } from "../../../../utils/constants/UI";
import { STRINGS } from "../../../Marketing/strings";
import { styles } from "./styles";

// `items` shape: [{ account_id: string, fromGroupId: string | null }]
// Each entry uses /group/add-email when fromGroupId is null, otherwise /group/move-email.
const MoveToGroupMenu = ({
  anchorEl,
  open,
  onClose,
  onMoved,
  onCreateGroupClick,
  items = [],
  currentGroupId,
  groups = [],
}) => {
  const classes = styles();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [targetId, setTargetId] = useState("");
  const [isMoving, setIsMoving] = useState(false);

  // Targets exclude the group the selection is currently in.
  const eligibleGroups = useMemo(
    () => groups.filter((g) => g._id !== currentGroupId),
    [groups, currentGroupId],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return eligibleGroups;
    return eligibleGroups.filter((g) =>
      (g.group_name || "").toLowerCase().includes(q),
    );
  }, [eligibleGroups, search]);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTargetId("");
    }
  }, [open]);

  const handleMove = async () => {
    if (!targetId || !items.length) return;
    try {
      setIsMoving(true);
      const results = await Promise.allSettled(
        items.map((it) =>
          it.fromGroupId
            ? postRequest(
                navigate,
                CampaignSettingsEndpoints.mailboxGroupMoveEmail,
                { to_group_id: targetId, account_id: it.account_id },
              )
            : postRequest(
                navigate,
                CampaignSettingsEndpoints.mailboxGroupAddEmail,
                { group_id: targetId, account_id: it.account_id },
              ),
        ),
      );

      const successCount = results.filter((r) => r.status === "fulfilled").length;
      const failures = results.filter((r) => r.status === "rejected");

      if (successCount > 0) {
        EventEmitter.emit(
          EMITTER_SUCCESS,
          `${successCount} mailbox${successCount > 1 ? "es" : ""} moved`,
        );
      }
      if (failures.length > 0) {
        const first = failures[0].reason;
        EventEmitter.emit(
          EMITTER_ERROR,
          `${failures.length} couldn't be moved: ${getErrorMessage(
            first?.data?.data?.errors || {},
            first?.data?.message,
          )}`,
        );
      }

      onMoved?.(targetId);
      onClose?.();
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
      );
    } finally {
      setIsMoving(false);
    }
  };

  return (
    <CustomStyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      width="320px"
      noMargin
    >
      <Box className={classes.mgMoveMenu}>
        <Box className={classes.mgMoveMenuHeader}>
          {STRINGS.MOVE_TO_GROUP_MENU_TITLE}
        </Box>
        <Box className={classes.mgMoveMenuSearch}>
          <SearchField
            autoComplete="off"
            size={"sm36"}
            placeholder={STRINGS.GROUPS_SEARCH_PLACEHOLDER}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>
        <Box className={classes.mgMoveMenuList}>
          {filtered.length === 0 ? (
            <Box className={classes.mgMoveMenuEmpty}>
              {eligibleGroups.length === 0
                ? STRINGS.MOVE_TO_GROUP_MENU_EMPTY
                : STRINGS.NO_GROUPS_MATCH}
            </Box>
          ) : (
            filtered.map((g) => {
              const isActive = g._id === targetId;
              return (
                <Box
                  key={g._id}
                  className={`${classes.mgMoveMenuItem} ${
                    isActive ? classes.mgMoveMenuItemActive : ""
                  }`}
                  onClick={() => setTargetId(g._id)}
                >
                  <IconComponent
                    iconLabel={ICON_LABELS.FOLDER}
                    color={WARNING.W_500}
                    fontSize={fontSize.LG}
                  />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={g.group_name}
                  >
                    {g.group_name}
                  </span>
                </Box>
              );
            })
          )}
        </Box>
        <Box className={classes.mgMoveMenuFooter}>
          <Button
            label={STRINGS.CREATE_GROUP}
            LeftIcon={ICON_LABELS.ADD}
            variant={"text"}
            size={"sm36"}
            onClick={() => {
              onClose?.();
              onCreateGroupClick?.();
            }}
          />
          <Box className={classes.mgMoveMenuFooterActions}>
            <Button
              label={STRINGS.CANCEL}
              variant={"ghost"}
              size={"sm36"}
              onClick={onClose}
            />
            <Button
              label={STRINGS.ACTION_MOVE}
              variant={"primary"}
              size={"sm36"}
              onClick={handleMove}
              disabled={!targetId || items.length === 0}
              isLoading={isMoving}
            />
          </Box>
        </Box>
      </Box>
    </CustomStyledMenu>
  );
};

export default MoveToGroupMenu;
